import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PasSystemProcessComponent } from '@porsche-aftersales/frontend-toolbox';
import { action, observable } from 'mobx-angular';
import { lastValueFrom } from 'rxjs';

import { AttachmentModel } from '../../content/recourse-booking/model/attachment.model';
import { RecourseCampaignDetailsModel } from '../../content/recourse-campaign/model/recourse-campaign-details-model';
import { RecourseCampaignModel } from '../../content/recourse-campaign/model/recourse-campaign-model';
import { RecourseCampaignStateModel } from '../../content/recourse-campaign/model/recourse-campaign-state-model';
import { UserByRightModel } from '../../content/recourse-campaign/model/user-by-right-model';
import { DateFormatPipe } from '../../pipes/date-format.pipe';
import { CampaignStateEnum } from '../../shared/enums/campaign-state-enum';
import { getEnumStringValue } from '../../shared/enums/enum-util-functions/enum-utils';
import { TopicEnum } from '../../shared/enums/topic-enum';
import { AttachmentService } from '../attachment/attachment.service';
import {
  handleVerification,
  showAttachmentUploadFailed,
  showAttachmentUploadSuccess,
} from '../booking/recourse-booking-functions';
import { NotificationServiceWrapper } from '../notification-service-wrapper/notification-service-wrapper';
import { UserRights } from '../user-service/user-rights.enum';
import { UserService } from '../user-service/user.service';

import { translateCampaignStateType } from './recourse-campaign-functions';
import { RecourseCampaignService } from './recourse-campaign.service';

@Injectable({
  providedIn: 'root',
})
export class RecourseCampaignStore {
  private readonly recourseCampaignService = inject(RecourseCampaignService);
  private readonly dateFormatPipe = inject(DateFormatPipe);
  private readonly translateService = inject(TranslateService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly userService = inject(UserService);
  private readonly attachmentService = inject(AttachmentService);

  private static readonly RECOURSE_CAMPAIGN_ASSIGNED_USER_RIGHT = 'TuRRecourseCockpit.Campaigns.Workflow';
  private static readonly NOTIFICATION_UPDATE_SUCCESS = 'notification_update_success';
  private static readonly NOTIFICATION_UPDATE_ERROR_3004 = 'notification_update_error_3004';
  private static readonly NOTIFICATION_UPDATE_ERROR_MISC = 'notification_update_error_misc';
  private static readonly NOTIFICATION_UPDATE_ERROR_UNEXPECTED = 'notification_update_error_unexpected';

  @observable /* For overview. Contains also values from CampaignEx table*/
  public recourseCampaignList: RecourseCampaignModel[];

  @observable /* Contains User-Rights*/ public userRights: UserRights[];

  @observable /* Selected value from OverviewTable*/ private selectedRecourseCampaign: RecourseCampaignModel;

  @observable /* The necessary values from CampaignEx-Table for edit mode*/
  public selectedRecourseCampaignEdit: RecourseCampaignDetailsModel;

  @observable /* Users which can be assigned to campaign recourse*/
  //  public userByRightListForSelection: UserByRightModel = null;
  public userByRightListForSelection: any[] = [];

  @observable /* States which can be assigned to campaign recourse by selectBox*/
  public recourseCampaignStateListForSelection: any[] = [];

  @observable
  public generalDataAttachments: AttachmentModel[];

  @observable
  public complaintAttachments: AttachmentModel[];

  @observable
  public recourseClaimAttachments: AttachmentModel[];

  @observable
  public negotiationAttachments: AttachmentModel[];

  @action /* Is executed with every initial call and stores the values in variable*/
  public async getRecourseCampaigns(): Promise<RecourseCampaignModel[]> {
    this.recourseCampaignList = await lastValueFrom(this.recourseCampaignService.getRecourseCampaigns());
    return this.recourseCampaignList;
  }

  @action
  public async getGeneralDataAttachments(): Promise<AttachmentModel[]> {
    this.generalDataAttachments = await lastValueFrom(
      this.attachmentService.getAttachmentsByCampaignAndTopic(
        this.getAttachemntsCampaignId(),
        getEnumStringValue(TopicEnum, 0),
      ),
    );
    this.generalDataAttachments.forEach(attachment => {
      handleVerification(this.notificationServiceWrapper, this.translateService, attachment);
    });
    return this.generalDataAttachments;
  }

  private getAttachemntsCampaignId(): string {
    let exId = this.selectedRecourseCampaignEdit.recourseCampaignExId;
    if (!exId) {
      exId = this.selectedRecourseCampaignEdit.campaignRecourseId;
    }
    return exId;
  }

  @action
  public async getComplaintAttachments(): Promise<AttachmentModel[]> {
    this.complaintAttachments = await lastValueFrom(
      this.attachmentService.getAttachmentsByCampaignAndTopic(
        this.getAttachemntsCampaignId(),
        getEnumStringValue(TopicEnum, 1),
      ),
    );
    this.complaintAttachments.forEach(attachment => {
      handleVerification(this.notificationServiceWrapper, this.translateService, attachment);
    });
    return this.complaintAttachments;
  }

  @action
  public async getRecourseClaimAttachments(): Promise<AttachmentModel[]> {
    this.recourseClaimAttachments = await lastValueFrom(
      this.attachmentService.getAttachmentsByCampaignAndTopic(
        this.getAttachemntsCampaignId(),
        getEnumStringValue(TopicEnum, 2),
      ),
    );
    this.recourseClaimAttachments.forEach(attachment => {
      handleVerification(this.notificationServiceWrapper, this.translateService, attachment);
    });
    return this.recourseClaimAttachments;
  }

  @action
  public async getNegotiationAttachments(): Promise<AttachmentModel[]> {
    this.negotiationAttachments = await lastValueFrom(
      this.attachmentService.getAttachmentsByCampaignAndTopic(
        this.getAttachemntsCampaignId(),
        getEnumStringValue(TopicEnum, 3),
      ),
    );
    this.negotiationAttachments.forEach(attachment => {
      handleVerification(this.notificationServiceWrapper, this.translateService, attachment);
    });
    return this.negotiationAttachments;
  }

  /* Is executed with every initial call and stores the values in variable*/
  @action
  public getCurrentUserRights(): void {
    this.userRights = this.userService.getRights();
  }

  @action /* Is executed when clicking on edit */ public async getRecourseCampaignDetails(
    campaignRecourseId: string,
  ): Promise<boolean> {
    try {
      /* set selectedRecourseCampaignItem via the local store */
      this.selectedRecourseCampaign = this.recourseCampaignList.find(x => x.campaignRecourseId === campaignRecourseId);
      /* set selectedRecourseCampaignExItem */
      this.selectedRecourseCampaignEdit = await lastValueFrom(
        this.recourseCampaignService.getRecourseCampaignEx(campaignRecourseId),
      );
      /* extend with already existing data */
      this.extendWithExistigData();
      /* prepare the CampaignAssignedUser options for selection*/
      if (this.userByRightListForSelection.length === 0) {
        const resultUserByRightModel: UserByRightModel = await lastValueFrom(
          this.recourseCampaignService.getUserByRight(RecourseCampaignStore.RECOURSE_CAMPAIGN_ASSIGNED_USER_RIGHT),
        );
        for (const index in resultUserByRightModel) {
          this.userByRightListForSelection.push({
            key: resultUserByRightModel[index].userId,
            value: `${resultUserByRightModel[index].firstName} ${resultUserByRightModel[index].lastName}`,
          });
        }
      }
      /* prepare the CampaignState options for selection*/
      if (this.recourseCampaignStateListForSelection.length === 0) {
        const resultRecourseCampaignStateModel: RecourseCampaignStateModel = await lastValueFrom(
          this.recourseCampaignService.getRecourseCampaignStateList(),
        );
        for (const index in resultRecourseCampaignStateModel) {
          this.recourseCampaignStateListForSelection.push({
            key: resultRecourseCampaignStateModel[index].pkCampaignStateId,
            value: `${
              resultRecourseCampaignStateModel[index].pkCampaignStateId === CampaignStateEnum.NEW_CAMPAIGN
                ? 0
                : resultRecourseCampaignStateModel[index].pkCampaignStateId
            } - ${this.translateService.instant(resultRecourseCampaignStateModel[index].campaignStateDescription)}`,
          });
        }
      }
      return true;
    } catch {
      this.showNotification(RecourseCampaignStore.NOTIFICATION_UPDATE_ERROR_UNEXPECTED, null);
      return false;
    }
  }

  @action /* Is executed when clicking on edit */ public async getRecourseCampaignExDetails(
    campaignRecourseId: string,
    campaignRecourseExId: string,
  ): Promise<boolean> {
    try {
      /* set selectedRecourseCampaignItem via the local store */
      this.selectedRecourseCampaign = this.recourseCampaignList.find(
        x => x.recourseCampaignExId === campaignRecourseExId,
      );
      /* set selectedRecourseCampaignExItem */
      this.selectedRecourseCampaignEdit = await lastValueFrom(
        this.recourseCampaignService.getRecourseCampaignExDetails(campaignRecourseId, campaignRecourseExId),
      );
      /* extend with already existing data */
      this.extendWithExistigData();
      /* prepare the CampaignAssignedUser options for selection*/
      if (this.userByRightListForSelection.length === 0) {
        const resultUserByRightModel: UserByRightModel = await lastValueFrom(
          this.recourseCampaignService.getUserByRight(RecourseCampaignStore.RECOURSE_CAMPAIGN_ASSIGNED_USER_RIGHT),
        );
        for (const index in resultUserByRightModel) {
          this.userByRightListForSelection.push({
            key: resultUserByRightModel[index].userId,
            value: `${resultUserByRightModel[index].firstName} ${resultUserByRightModel[index].lastName}`,
          });
        }
      }
      /* prepare the CampaignState options for selection*/
      if (this.recourseCampaignStateListForSelection.length === 0) {
        const resultRecourseCampaignStateModel: RecourseCampaignStateModel = await lastValueFrom(
          this.recourseCampaignService.getRecourseCampaignStateList(),
        );
        for (const index in resultRecourseCampaignStateModel) {
          this.recourseCampaignStateListForSelection.push({
            key: resultRecourseCampaignStateModel[index].pkCampaignStateId,
            value: `${
              resultRecourseCampaignStateModel[index].pkCampaignStateId === CampaignStateEnum.NEW_CAMPAIGN
                ? 0
                : resultRecourseCampaignStateModel[index].pkCampaignStateId
            } - ${this.translateService.instant(resultRecourseCampaignStateModel[index].campaignStateDescription)}`,
          });
        }
      }
      return true;
    } catch {
      this.showNotification(RecourseCampaignStore.NOTIFICATION_UPDATE_ERROR_UNEXPECTED, null);
      return false;
    }
  }

  /**
   * Is executed when the user wants to upload a file
   **/
  @action
  public async upload(formData: FormData, processComponent: PasSystemProcessComponent): Promise<any> {
    let resObj: any;
    processComponent.startProcess();
    await lastValueFrom(this.attachmentService.upload(formData))
      .then((result: any) => {
        resObj = result;
        processComponent.stopProcess();
        showAttachmentUploadSuccess(this.notificationServiceWrapper, this.translateService, formData.get('filename'));
      })
      .catch(error => {
        processComponent.stopProcess();
        showAttachmentUploadFailed(
          this.notificationServiceWrapper,
          this.translateService,
          formData.get('filename'),
          error,
        );
      });
    return resObj;
  }

  /*
    Extends the selectedRecourseCampaignEdit
   */
  private extendWithExistigData(): void {
    this.selectedRecourseCampaignEdit.campaignRecourseId = this.selectedRecourseCampaign.campaignRecourseId;
    this.selectedRecourseCampaignEdit.recourseCampaignExId = this.selectedRecourseCampaign.recourseCampaignExId;
    this.selectedRecourseCampaignEdit.operationId = this.selectedRecourseCampaign.operationId;
    this.selectedRecourseCampaignEdit.topic = this.selectedRecourseCampaign.topic;
    this.selectedRecourseCampaignEdit.affectedVehicles = this.selectedRecourseCampaign.affectedVehicles;
    this.selectedRecourseCampaignEdit.targetCars = this.selectedRecourseCampaign.targetCars;
    this.selectedRecourseCampaignEdit.publishedDate = this.selectedRecourseCampaign.publishedDate;
    this.selectedRecourseCampaignEdit.name1 = this.selectedRecourseCampaign.name1;
    this.selectedRecourseCampaignEdit.assignee = this.selectedRecourseCampaign.assignee;
    this.selectedRecourseCampaignEdit.campaignCostsPrognosis = this.selectedRecourseCampaign.campaignCostsPrognosis;
    this.selectedRecourseCampaignEdit.businessYear = this.selectedRecourseCampaign.businessYear;
    this.selectedRecourseCampaignEdit.fulfillmentDegree = this.selectedRecourseCampaign.fulfillmentDegree;
    this.selectedRecourseCampaignEdit.fkCampaignStateId = this.selectedRecourseCampaign.fkCampaignStateId;
    this.selectedRecourseCampaignEdit.campaignStateType = translateCampaignStateType(
      this.selectedRecourseCampaign.campaignStateType,
      this.translateService,
    );
    this.selectedRecourseCampaignEdit.campaignCreateDate = this.selectedRecourseCampaign.campaignCreateDate;
    this.selectedRecourseCampaignEdit.orderType = this.selectedRecourseCampaign.orderType;
    this.selectedRecourseCampaignEdit.supplier = this.selectedRecourseCampaign.supplier;
  }

  @action /* Is executed when clicking on save */ public async saveRecourseCampaignDetails(obj: any): Promise<boolean> {
    let retVal = true;
    this.acceptChanges(obj);
    /* Not sure with that solution. Question: How read 403 FORBIDDEN Forbidden,ErrorResponse(errorCode=3004, message=User does not have campaigns workflow permission.*/
    await lastValueFrom(this.recourseCampaignService.updateRecourseCampaign(this.selectedRecourseCampaignEdit))
      .then((result: RecourseCampaignDetailsModel) => {
        this.selectedRecourseCampaign = this.recourseCampaignList.find(
          x => x.campaignRecourseId === obj.campaignRecourseId,
        );
        if (this.selectedRecourseCampaign) {
          this.selectedRecourseCampaign.recourseCampaignExId = result.recourseCampaignExId;
        }
        this.updateSelectedRecourseCampaign(result);
        this.selectedRecourseCampaignEdit = result;
        this.showCampaignSaveSuccess(this.selectedRecourseCampaignEdit.campaignRecourseId);
      })
      .catch(() => {
        this.showCampaignSaveFailed(this.selectedRecourseCampaignEdit.campaignRecourseId);
        retVal = false;
      });
    return retVal;
  }

  private acceptChanges(obj: any): void {
    const recourseCampaignStateKey = obj.recourseCampaignState.key;
    if (
      CampaignStateEnum.CANCELLED_STOPPED_CHANGED === recourseCampaignStateKey ||
      CampaignStateEnum.NO_SUPPLIER_FAULT === recourseCampaignStateKey
    ) {
      this.selectedRecourseCampaignEdit.fkCampaignStateId = recourseCampaignStateKey;
    }
    this.selectedRecourseCampaignEdit.assignee = obj.assignee ? obj.assignee.key : null;
    this.selectedRecourseCampaignEdit.orderType = obj.orderType;
    this.selectedRecourseCampaignEdit.fkCampaignStateId = recourseCampaignStateKey;
    this.selectedRecourseCampaignEdit.supplier = obj.supplier;
    this.selectedRecourseCampaignEdit.costsPrognosisInternal = obj.costsPrognosisInternal;
    this.selectedRecourseCampaignEdit.markets = obj.markets;
    this.selectedRecourseCampaignEdit.contactInternal = obj.contactInternal;
    this.selectedRecourseCampaignEdit.contactExternal = obj.contactExternal;
    this.selectedRecourseCampaignEdit.recourseCoordination = obj.recourseCoordination;
    this.selectedRecourseCampaignEdit.recourseActualCostsEur = obj.recourseActualCostsEur;
    this.selectedRecourseCampaignEdit.costEditDate =
      obj.costEditDate === null
        ? null
        : new Date(Date.parse(this.dateFormatPipe.transformDateToGlobalFormat(obj.costEditDate)));
    this.selectedRecourseCampaignEdit.partsRequest = obj.partsRequest;
    this.selectedRecourseCampaignEdit.comment = obj.comment;
    this.selectedRecourseCampaignEdit.complaintDate =
      obj.complaintDate === null
        ? null
        : new Date(Date.parse(this.dateFormatPipe.transformDateToGlobalFormat(obj.complaintDate)));
    this.selectedRecourseCampaignEdit.revenueOthersEur = obj.revenueOthersEur;
    this.selectedRecourseCampaignEdit.recourseClaimDate =
      obj.recourseClaimDate === null
        ? null
        : new Date(Date.parse(this.dateFormatPipe.transformDateToGlobalFormat(obj.recourseClaimDate)));
    this.selectedRecourseCampaignEdit.affectedVehicles = obj.affectedVehicles;
    this.selectedRecourseCampaignEdit.recourseCampaignExId = obj.recourseCampaignExId;
  }

  private updateSelectedRecourseCampaign(obj: any): void {
    this.selectedRecourseCampaign.orderType = obj.orderType;
    this.selectedRecourseCampaign.fkCampaignStateId = obj.fkCampaignStateId;
    if (obj.assignee) {
      this.selectedRecourseCampaign.assignee = obj.assignee;
    }
    this.selectedRecourseCampaign.supplier = obj.supplier;
    this.selectedRecourseCampaign.affectedVehicles = obj.affectedVehicles;
  }

  private showCampaignSaveSuccess(campaignId: any): void {
    this.translateService
      .get(['TITLE_SAVED', 'SAVED_CAMPAIGN_SUCCESSFUL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.TITLE_SAVED;
        const content: string = this.translateService.instant('SAVED_CAMPAIGN_SUCCESSFUL', { 0: campaignId });

        this.notificationServiceWrapper.addSuccessNotification(title, content);
      });
  }

  private showCampaignSaveFailed(bookingId: any): void {
    this.translateService
      .get(['TITLE_FAILED', 'SAVED_CAMPAIGN_FAILED'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.TITLE_FAILED;
        const content: string = this.translateService.instant('SAVED_CAMPAIGN_FAILED', { 0: bookingId });

        this.notificationServiceWrapper.addErrorNotification(title, content);
      });
  }

  private showNotification(type: string, errorCode: string): void {
    switch (type) {
      case RecourseCampaignStore.NOTIFICATION_UPDATE_SUCCESS: {
        this.notificationServiceWrapper.addSuccessNotification(
          this.translateService.instant('SAVED_TITLE'),
          this.translateService.instant('SAVED_SUCCESSFUL'),
        );
        break;
      }
      case RecourseCampaignStore.NOTIFICATION_UPDATE_ERROR_3004: {
        this.notificationServiceWrapper.addErrorNotification(
          `${this.translateService.instant('NOTIFICATION_SAVE_ERROR_HEADER')} (ErrorCode:${errorCode}) `,
          `${this.translateService.instant('NOTIFICATION_SAVE_ERROR_MESSAGE')} [${this.translateService.instant(
            'NOTIFICATION_SAVE_ERROR_MESSAGE_REASON_PERMISSON',
          )}]`,
        );
        break;
      }
      case RecourseCampaignStore.NOTIFICATION_UPDATE_ERROR_MISC: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_SAVE_ERROR_HEADER'),
          this.translateService.instant('NOTIFICATION_SAVE_ERROR_MESSAGE'),
        );
        break;
      }
      case RecourseCampaignStore.NOTIFICATION_UPDATE_ERROR_UNEXPECTED: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_HEADER'),
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_MESSAGE'),
        );
        break;
      }
      // no default
    }
  }

  public async getQppSysReserveUrl(): Promise<string> {
    let resObj = '';
    await lastValueFrom(this.recourseCampaignService.getQppSysReserveUrl()).then(data => {
      resObj = data;
    });
    return resObj;
  }
}
